import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutGridIcon, ListIcon, SearchIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { CATEGORIES, COLORS, PRODUCTS } from '../data/products';
import { CategoryId } from '../types';
import { ProductCard } from '../components/ProductCard';
type SortKey = 'vendidos' | 'menor' | 'mayor' | 'novedades';
const SORTS: {
  id: SortKey;
  label: string;
}[] = [{
  id: 'vendidos',
  label: 'Más vendidos'
}, {
  id: 'menor',
  label: 'Precio menor'
}, {
  id: 'mayor',
  label: 'Precio mayor'
}, {
  id: 'novedades',
  label: 'Novedades'
}];
const MAX_PRICE = 5000;
const PAGE_SIZE = 9;
export function Catalog() {
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') as CategoryId | null;
  const [cats, setCats] = useState<CategoryId[]>(initialCat ? [initialCat] : []);
  const [maxPrice, setMaxPrice] = useState(MAX_PRICE);
  const [colorIds, setColorIds] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('vendidos');
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const toggle = <T,>(arr: T[], value: T): T[] => arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
  const filtered = useMemo(() => {
    const result = PRODUCTS.filter(p => {
      const price = p.salePrice ?? p.price;
      if (cats.length && !cats.includes(p.category)) return false;
      if (price > maxPrice) return false;
      if (colorIds.length && !p.colors.some(c => colorIds.includes(c.id))) return false;
      if (query && !`${p.name} ${p.shortDescription} ${p.categoryLabel}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
    return result.sort((a, b) => {
      const pa = a.salePrice ?? a.price;
      const pb = b.salePrice ?? b.price;
      if (sort === 'menor') return pa - pb;
      if (sort === 'mayor') return pb - pa;
      if (sort === 'novedades') return Number(!!b.isNew) - Number(!!a.isNew);
      return b.reviews - a.reviews;
    });
  }, [cats, maxPrice, colorIds, query, sort]);
  const shown = filtered.slice(0, visible);
  const activeFilters = cats.length + colorIds.length + (maxPrice < MAX_PRICE ? 1 : 0);
  const clearAll = () => {
    setCats([]);
    setColorIds([]);
    setMaxPrice(MAX_PRICE);
    setQuery('');
  };
  const filterPanel = <div className="space-y-9" data-id="element-845" data-mp="xanh6g9dem33">
      <fieldset data-id="element-846" data-mp="x9cc8d46v8ae">
        <legend className="font-display text-lg uppercase tracking-[0.18em] text-white" data-id="element-847" data-mp="xbylqian3xv9">
          Categoría
        </legend>
        <div className="mt-4 space-y-3" data-id="element-848" data-mp="3i0cuupyc81u7">
          {CATEGORIES.map(cat => <label key={cat.id} className="flex cursor-pointer items-center gap-3 text-sm text-white/60 transition-colors hover:text-white" data-id="element-849" data-mp="3i2fvs5113fdp">
              <input type="checkbox" checked={cats.includes(cat.id)} onChange={() => {
            setCats(prev => toggle(prev, cat.id));
            setVisible(PAGE_SIZE);
          }} className="h-4 w-4 shrink-0 appearance-none border border-white/25 bg-transparent checked:border-pink-500 checked:bg-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500" data-id="element-850" data-mp="3egcge4wly72a" />
              <span className="flex-1" data-id="element-851" data-mp="2qtmed3k9echo">{cat.short}</span>
              <span className="text-xs text-white/25" data-id="element-852" data-mp="2qqqj5mzsq1wx">{cat.count}</span>
            </label>)}
        </div>
      </fieldset>

      <fieldset className="border-t border-white/10 pt-8" data-id="element-853" data-mp="30qhaxgwz22tb">
        <legend className="font-display text-lg uppercase tracking-[0.18em] text-white" data-id="element-854" data-mp="30oxv1rf89ts2">
          Precio
        </legend>
        <input type="range" min={500} max={MAX_PRICE} step={100} value={maxPrice} onChange={e => {
        setMaxPrice(Number(e.target.value));
        setVisible(PAGE_SIZE);
      }} aria-label="Precio máximo" className="zadar-range mt-6" data-id="element-855" data-mp="282d1ckba55lc" />
        <div className="mt-3 flex items-center justify-between text-xs text-white/45" data-id="element-856" data-mp="1k9tp2ybz8bco">
          <span data-id="element-857" data-mp="1p34viycvmq42">$ 500</span>
          <span className="font-semibold text-white" data-id="element-858" data-mp="1p3eh89qw2b3y">
            Hasta $ {maxPrice.toLocaleString('es-UY')}
          </span>
        </div>
      </fieldset>

      <fieldset className="border-t border-white/10 pt-8" data-id="element-859" data-mp="1yzhpzyg8x7zv">
        <legend className="font-display text-lg uppercase tracking-[0.18em] text-white" data-id="element-860" data-mp="1z23yq1evhtjo">
          Color
        </legend>
        <div className="mt-4 flex gap-3" data-id="element-861" data-mp="280pg3t8lz2iv">
          {COLORS.map(color => {
          const active = colorIds.includes(color.id);
          return <button key={color.id} type="button" onClick={() => {
            setColorIds(prev => toggle(prev, color.id));
            setVisible(PAGE_SIZE);
          }} aria-label={color.name} aria-pressed={active} className={`flex h-9 w-9 items-center justify-center rounded-full border transition-all ${active ? 'border-white scale-105' : 'border-white/20 hover:border-white/50'}`} data-id="element-862" data-mp="9n5xifwz4ahj">
                <span className="h-5 w-5 rounded-full" style={{
              backgroundColor: color.hex
            }} data-id="element-863" data-mp="p1hytzgnunu6" />
              </button>;
        })}
        </div>
      </fieldset>

      <fieldset className="border-t border-white/10 pt-8" data-id="element-864" data-mp="g3fpmwmrwovt">
        <legend className="font-display text-lg uppercase tracking-[0.18em] text-white" data-id="element-865" data-mp="b206ja45osg2">
          Ordenar por
        </legend>
        <div className="mt-4 space-y-3" data-id="element-866" data-mp="30pr58aa40hla">
          {SORTS.map(option => <label key={option.id} className="flex cursor-pointer items-center gap-3 text-sm text-white/60 transition-colors hover:text-white" data-id="element-867" data-mp="2vr64m9qy3vwb">
              <input type="radio" name="sort" checked={sort === option.id} onChange={() => setSort(option.id)} className="h-4 w-4 shrink-0 appearance-none rounded-full border border-white/25 checked:border-[5px] checked:border-pink-500 focus:outline-none" data-id="element-868" data-mp="327of2xvn07t6" />
              {option.label}
            </label>)}
        </div>
      </fieldset>

      {activeFilters > 0 && <button type="button" onClick={clearAll} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white/70 transition-colors hover:border-pink-500 hover:text-white" data-id="element-869" data-mp="24m0f62zz37cw">
          <XIcon className="h-3.5 w-3.5" data-id="element-870" data-mp="20k5ff2xox218" /> Limpiar filtros
        </button>}
    </div>;
  return <div className="w-full bg-black" data-id="element-871" data-mp="1gxc2jjxtua8u">
      <div className="border-b border-white/10 bg-ink-900" data-id="element-872" data-mp="1gydkm8d9fx02">
        <div className="mx-auto w-full max-w-7xl px-5 py-12 lg:px-8 lg:py-16" data-id="element-873" data-mp="1bzk8okdmge20">
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-pink-500" data-id="element-874" data-mp="1pya6y3c6gjry">
            Catálogo
          </span>
          <h1 className="mt-3 font-display text-5xl uppercase leading-none text-white sm:text-6xl" data-id="element-875" data-mp="1lvvlrcl1d7xi">
            Todos los productos
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/50" data-id="element-876" data-mp="1zud5z0bqg2ao">
            Bandas, guantes, tobilleras y correctores diseñados para entrenar
            fuerte sin castigar el cuerpo.
          </p>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-7xl gap-10 px-5 py-10 lg:px-8 lg:py-14" data-id="element-877" data-mp="24n6b3pb45cna">
        {/* SIDEBAR */}
        <aside className="hidden w-64 shrink-0 lg:block" data-id="element-878" data-mp="2io8zi8pkyz1d">
          <div className="sticky top-28" data-id="element-879" data-mp="2do4k6m0esacq">{filterPanel}</div>
        </aside>

        {/* MAIN */}
        <div className="min-w-0 flex-1" data-id="element-880" data-mp="3l0k3ymjlo1u0">
          {/* TOP BAR */}
          <div className="flex flex-col gap-4" data-id="element-881" data-mp="3l0aed02gvwou">
            <div className="relative" data-id="element-882" data-mp="3pz55qqei2tkk">
              <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" data-id="element-883" data-mp="3py3k5rzuyool" />
              <input type="search" value={query} onChange={e => {
              setQuery(e.target.value);
              setVisible(PAGE_SIZE);
            }} placeholder="Buscar productos..." aria-label="Buscar productos" className="w-full border border-white/10 bg-ink-800 py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-white/35 focus:border-pink-500 focus:outline-none" data-id="element-884" data-mp="3tz5najwwbkvu" />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-885" data-mp="2imcv58070uf9">
              <p className="text-xs uppercase tracking-[0.14em] text-white/45" data-id="element-886" data-mp="2ik8ssuhy03h3">
                Mostrando {shown.length} de {filtered.length} productos
              </p>
              <div className="flex items-center gap-2" data-id="element-887" data-mp="2g1541xhagr0m">
                <button type="button" onClick={() => setFiltersOpen(true)} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-white/70 transition-colors hover:border-pink-500 hover:text-white lg:hidden" data-id="element-888" data-mp="2b229x82uk57f">
                  <SlidersHorizontalIcon className="h-4 w-4" data-id="element-889" data-mp="1x43wq609ieti" />
                  Filtros{activeFilters ? ` (${activeFilters})` : ''}
                </button>
                <div className="flex border border-white/10" data-id="element-890" data-mp="1ncg8edz05dw9">
                  <button type="button" onClick={() => setView('grid')} aria-label="Vista de cuadrícula" aria-pressed={view === 'grid'} className={`p-2.5 transition-colors ${view === 'grid' ? 'bg-pink-500 text-white' : 'text-white/45 hover:text-white'}`} data-id="element-891" data-mp="1nan24zsawvos">
                    <LayoutGridIcon className="h-4 w-4" data-id="element-892" data-mp="p5jyuc1r4et9" />
                  </button>
                  <button type="button" onClick={() => setView('list')} aria-label="Vista de lista" aria-pressed={view === 'list'} className={`p-2.5 transition-colors ${view === 'list' ? 'bg-pink-500 text-white' : 'text-white/45 hover:text-white'}`} data-id="element-893" data-mp="13txzcgbp3r6n">
                    <ListIcon className="h-4 w-4" data-id="element-894" data-mp="g5ohtqlilplb" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* GRID */}
          {shown.length === 0 ? <div className="mt-16 border border-dashed border-white/15 px-6 py-20 text-center" data-id="element-895" data-mp="1rl3e0s5wgdfd">
              <h2 className="font-display text-3xl uppercase text-white" data-id="element-896" data-mp="1z4cq4t5siq84">
                Sin resultados
              </h2>
              <p className="mx-auto mt-3 max-w-sm text-sm text-white/45" data-id="element-897" data-mp="2447cqkij8tnd">
                No encontramos productos con esos filtros. Probá ampliar el rango
                de precio o quitar algún color.
              </p>
              <button type="button" onClick={clearAll} className="mt-6 bg-pink-500 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-898" data-mp="1u5i47icj0ljs">
                Limpiar filtros
              </button>
            </div> : <div className={`mt-8 ${view === 'grid' ? 'grid gap-5 sm:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-5'}`} data-id="element-899" data-mp="2d3m3zm6ix88x">
              {shown.map(product => <ProductCard key={product.id} product={product} view={view} data-id="element-900" data-mp="35lgbbgo58xid" />)}
            </div>}

          {visible < filtered.length && <div className="mt-12 flex justify-center" data-id="element-901" data-mp="3bflg2t585q97">
              <button type="button" onClick={() => setVisible(v => v + PAGE_SIZE)} className="border border-white px-8 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-black" data-id="element-902" data-mp="36fgxmxkralgc">
                Cargar más productos
              </button>
            </div>}
        </div>
      </div>

      {/* MOBILE FILTERS */}
      {filtersOpen && <div className="fixed inset-0 z-[60] lg:hidden" data-id="element-903" data-mp="2np7m2x32xhbz">
          <div className="absolute inset-0 bg-black/70" onClick={() => setFiltersOpen(false)} data-id="element-904" data-mp="2iqny6r4xdvgz" />
          <div className="absolute inset-y-0 left-0 w-[86%] max-w-sm overflow-y-auto border-r border-white/10 bg-ink-900 p-6" data-id="element-905" data-mp="2dqa0a1x1g1wo">
            <div className="mb-8 flex items-center justify-between" data-id="element-906" data-mp="28u1ei5oc5agj">
              <h2 className="font-display text-2xl uppercase tracking-wide text-white" data-id="element-907" data-mp="1yn8kzbc4s55h">
                Filtros
              </h2>
              <button type="button" onClick={() => setFiltersOpen(false)} aria-label="Cerrar filtros" className="p-2 text-white/60 hover:text-white" data-id="element-908" data-mp="23oo613eb6bnv">
                <XIcon className="h-5 w-5" data-id="element-909" data-mp="1fxshcf55fwku" />
              </button>
            </div>
            {filterPanel}
            <button type="button" onClick={() => setFiltersOpen(false)} className="mt-10 w-full bg-pink-500 py-4 text-[11px] font-bold uppercase tracking-[0.18em] text-white" data-id="element-910" data-mp="1kvkblft9nus7">
              Ver {filtered.length} productos
            </button>
          </div>
        </div>}
    </div>;
}